import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { useClickOutside } from '../../lib/useClickOutside';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { ApiError } from '../../lib/api';
import type { CurrentUser } from '../../types/user';
import type {
  PromotionPlan,
  PromotionPlanTask,
  PromotionPlanTaskStatus,
} from '../../types/promotionPlan';
import { ApplyTemplateModal } from './ApplyTemplateModal';
import {
  TaskAssignmentPicker,
  assignmentLabel,
} from './TaskAssignmentPicker';
import {
  createTask,
  deleteTask,
  listTasks,
  listUsers,
  updateTask,
  type TaskCreatePayload,
  type TaskUpdatePayload,
} from './api';
import {
  TASK_STATUS_OPTIONS,
  changedPositions,
  formatTaskCompletion,
  reorderIds,
  sortTasksForDisplay,
  taskStatusBadge,
  taskStatusLabel,
} from './taskOrdering';

function fmtDate(iso: string | null): string {
  if (!iso) return 'No due date';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `Due ${d.toLocaleDateString()}`;
}

// `plan` is optional so existing callers/tests that only have a planId still
// work; when provided it unlocks the Apply Template flow (§4.1) — the modal
// needs the Plan's brand + promoStartDate to filter templates and resolve
// preview due dates.
export function PlanTasks({
  planId,
  plan,
}: {
  planId: string;
  plan?: PromotionPlan;
}) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [applyNotice, setApplyNotice] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const tasksQuery = useQuery({
    queryKey: ['plan', planId, 'tasks'],
    queryFn: () => listTasks(planId),
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => listUsers(),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['plan', planId, 'tasks'] });

  const patchMut = useMutation({
    mutationFn: ({
      taskId,
      patch,
    }: {
      taskId: string;
      patch: TaskUpdatePayload;
    }) => updateTask(planId, taskId, patch),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (taskId: string) => deleteTask(planId, taskId),
    onSuccess: invalidate,
  });

  // Drag-reorder: assign sequential positions to the dropped order and PATCH
  // only the rows whose position actually changed (changedPositions), then
  // refetch once. Native HTML5 DnD — single vertical list, no dependency.
  const reorderMut = useMutation({
    mutationFn: async (moves: Array<{ id: string; position: number }>) => {
      for (const m of moves) {
        await updateTask(planId, m.id, { position: m.position });
      }
    },
    onSuccess: invalidate,
  });

  if (tasksQuery.isLoading) return <Spinner />;
  if (tasksQuery.error) {
    return (
      <p className="text-sm text-red-600">
        Failed to load tasks: {(tasksQuery.error as Error).message}
      </p>
    );
  }

  const tasks = sortTasksForDisplay(tasksQuery.data ?? []);
  const users = usersQuery.data ?? [];
  const orderedIds = tasks.map((t) => t.id);

  const handleDrop = (targetId: string) => {
    if (!dragId) return;
    const newOrder = reorderIds(orderedIds, dragId, targetId);
    setDragId(null);
    const moves = changedPositions(tasksQuery.data ?? [], newOrder);
    if (moves.length > 0) reorderMut.mutate(moves);
  };

  const busy =
    patchMut.isPending || deleteMut.isPending || reorderMut.isPending;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button type="button" onClick={() => setShowCreate(true)}>
          + Add task
        </Button>
        {plan && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setApplyNotice(null);
              setShowApply(true);
            }}
          >
            Apply Template
          </Button>
        )}
      </div>

      {applyNotice && (
        <p className="mb-3 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {applyNotice}
        </p>
      )}

      {tasks.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No tasks yet.</p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              users={users}
              busy={busy}
              dragging={dragId === task.id}
              onDragStart={() => setDragId(task.id)}
              onDragEnd={() => setDragId(null)}
              onDropOn={() => handleDrop(task.id)}
              onPatch={(patch) =>
                patchMut.mutate({ taskId: task.id, patch })
              }
              onDelete={() => deleteMut.mutate(task.id)}
            />
          ))}
        </ul>
      )}

      {showCreate && (
        <TaskCreateModal
          planId={planId}
          users={users}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            invalidate();
          }}
        />
      )}

      {showApply && plan && (
        <ApplyTemplateModal
          plan={plan}
          onClose={() => setShowApply(false)}
          onApplied={(count) => {
            setShowApply(false);
            setApplyNotice(
              `Added ${count} task${count === 1 ? '' : 's'} from the template.`,
            );
            invalidate();
            // The apply bumps the Plan's updatedAt (audit event) — refresh
            // the Plan so any derived header/stat reflects it.
            qc.invalidateQueries({ queryKey: ['plan', planId] });
          }}
        />
      )}
    </div>
  );
}

function TaskRow({
  task,
  users,
  busy,
  dragging,
  onDragStart,
  onDragEnd,
  onDropOn,
  onPatch,
  onDelete,
}: {
  task: PromotionPlanTask;
  users: CurrentUser[];
  busy: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropOn: () => void;
  onPatch: (patch: TaskUpdatePayload) => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [kebabOpen, setKebabOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const kebabRef = useRef<HTMLDivElement | null>(null);
  useClickOutside([statusRef], () => setStatusMenuOpen(false), statusMenuOpen);
  useClickOutside([kebabRef], () => setKebabOpen(false), kebabOpen);

  const completed = task.status === 'completed';
  // Completion attribution (spec §5.4) — expanded view only. Resolve the
  // completedBy uid against the same users list the assignee dropdown uses;
  // unknown/null uid drops the "by …" suffix.
  const completedByUser =
    users.find((u) => u.uid === task.completedBy) ?? null;
  const completionLabel = formatTaskCompletion(
    task,
    completedByUser ? formatUserDisplayName(completedByUser, { full: true }) : null,
  );

  const commitTitle = () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (next && next !== task.title) onPatch({ title: next });
    else setTitleDraft(task.title);
  };

  return (
    <li
      draggable={!editingTitle}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDropOn();
      }}
      className={`rounded-md border bg-white ${
        dragging
          ? 'border-slate-400 opacity-60'
          : 'border-slate-200'
      }`}
    >
      <div className="flex items-start gap-2 p-3">
        <span
          aria-hidden
          className="mt-0.5 cursor-grab select-none text-slate-300"
          title="Drag to reorder"
        >
          ⠿
        </span>
        <input
          type="checkbox"
          checked={completed}
          disabled={busy}
          aria-label={completed ? 'Mark not started' : 'Mark completed'}
          className="mt-1 h-4 w-4"
          onChange={(e) =>
            onPatch({
              status: e.target.checked ? 'completed' : 'not_started',
            })
          }
        />

        <div className="min-w-0 flex-1">
          {editingTitle ? (
            <input
              type="text"
              value={titleDraft}
              autoFocus
              disabled={busy}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTitle();
                if (e.key === 'Escape') {
                  setTitleDraft(task.title);
                  setEditingTitle(false);
                }
              }}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setTitleDraft(task.title);
                setEditingTitle(true);
              }}
              className={`text-left text-sm font-medium ${
                completed
                  ? 'text-slate-400 line-through'
                  : 'text-slate-900'
              } hover:underline`}
              title="Click to rename"
            >
              {task.title}
            </button>
          )}

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span>{fmtDate(task.dueDate)}</span>
            <span>·</span>
            <span>
              {assignmentLabel(
                users,
                task.assignedUserId,
                task.assignedTeam,
              )}
            </span>
            <span>·</span>
            <div ref={statusRef} className="relative">
              <button
                type="button"
                onClick={() => setStatusMenuOpen((o) => !o)}
                className={`rounded-full px-2 py-0.5 ${taskStatusBadge(task.status)}`}
              >
                {taskStatusLabel(task.status)}
              </button>
              {statusMenuOpen && (
                <div className="absolute left-0 z-10 mt-1 w-40 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
                  {TASK_STATUS_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => {
                        setStatusMenuOpen(false);
                        if (o.value !== task.status) {
                          onPatch({ status: o.value });
                        }
                      }}
                      className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 ${
                        o.value === task.status
                          ? 'font-semibold text-slate-900'
                          : 'text-slate-600'
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div ref={kebabRef} className="relative">
          <button
            type="button"
            onClick={() => setKebabOpen((o) => !o)}
            aria-label="Task actions"
            className="px-2 text-slate-400 hover:text-slate-700"
          >
            ⋮
          </button>
          {kebabOpen && (
            <div className="absolute right-0 z-10 mt-1 w-32 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  setKebabOpen(false);
                  setConfirmDelete(true);
                }}
                className="block w-full px-3 py-1.5 text-left text-xs text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? 'Collapse task' : 'Expand task'}
          aria-expanded={expanded}
          className="px-1 text-slate-400 hover:text-slate-700"
        >
          {expanded ? '▾' : '▸'}
        </button>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-slate-100 p-3">
          {completionLabel && (
            <p className="text-xs text-slate-400">{completionLabel}</p>
          )}

          {task.description && (
            <p className="whitespace-pre-wrap text-sm text-slate-700">
              {task.description}
            </p>
          )}

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Assigned to
            </span>
            <TaskAssignmentPicker
              users={users}
              assignedUserId={task.assignedUserId}
              assignedTeam={task.assignedTeam}
              disabled={busy}
              onChange={(next) => onPatch(next)}
            />
          </label>

          <NotesEditor
            key={task.id}
            value={task.notes ?? ''}
            disabled={busy}
            onSave={(notes) => onPatch({ notes: notes || null })}
          />
        </div>
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          title={task.title}
          busy={busy}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            onDelete();
          }}
        />
      )}
    </li>
  );
}

// Autosave notes: debounce 700ms after the last keystroke, and flush on blur
// so a quick edit-then-navigate doesn't lose the change. Only fires when the
// draft actually differs from the last-saved value.
function NotesEditor({
  value,
  disabled,
  onSave,
}: {
  value: string;
  disabled: boolean;
  onSave: (notes: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const savedRef = useRef(value);

  useEffect(() => {
    if (draft === savedRef.current) return;
    const t = setTimeout(() => {
      savedRef.current = draft;
      onSave(draft);
    }, 700);
    return () => clearTimeout(t);
  }, [draft, onSave]);

  const flush = () => {
    if (draft !== savedRef.current) {
      savedRef.current = draft;
      onSave(draft);
    }
  };

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-500">
        Notes
      </span>
      <textarea
        value={draft}
        rows={3}
        disabled={disabled}
        placeholder="Notes…"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={flush}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
      />
    </label>
  );
}

function ConfirmDeleteModal({
  title,
  busy,
  onCancel,
  onConfirm,
}: {
  title: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-slate-900">Delete task</h3>
        <p className="mt-2 text-sm text-slate-600">
          Delete <span className="font-medium">{title}</span>? This can't be
          undone.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            variant="destructive"
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

function TaskCreateModal({
  planId,
  users,
  onClose,
  onCreated,
}: {
  planId: string;
  users: CurrentUser[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignment, setAssignment] = useState<{
    assignedUserId: string | null;
    assignedTeam: string | null;
  }>({ assignedUserId: null, assignedTeam: null });
  const [status, setStatus] = useState<PromotionPlanTaskStatus>(
    'not_started',
  );
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const payload: TaskCreatePayload = {
        title: title.trim(),
        description: description.trim() || null,
        notes: null,
        assignedUserId: assignment.assignedUserId,
        assignedTeam: assignment.assignedTeam,
        dueDate: dueDate || null,
        status,
      };
      return createTask(planId, payload);
    },
    onSuccess: onCreated,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">
          New task
        </h3>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim()) {
              setError('Title is required.');
              return;
            }
            setError(null);
            mut.mutate();
          }}
        >
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Title
            </span>
            <input
              type="text"
              value={title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Description
            </span>
            <textarea
              value={description}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">
                Due date
              </span>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">
                Status
              </span>
              <select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as PromotionPlanTaskStatus)
                }
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              >
                {TASK_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Assigned to
            </span>
            <TaskAssignmentPicker
              users={users}
              assignedUserId={assignment.assignedUserId}
              assignedTeam={assignment.assignedTeam}
              onChange={setAssignment}
            />
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mut.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? 'Adding…' : 'Add task'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
